<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { listMyAccess, requestRenewal, grantErrorMessage } from '@/api/my-access';
import type { MyGrant, GrantStatus } from '@/api/types';
import { formatDate } from '@/lib/datetime';

const grants = ref<MyGrant[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

const busyId = ref<string | null>(null);
const actionError = ref<string | null>(null);

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

async function load() {
    loading.value = true;
    error.value = null;
    try {
        const res = await listMyAccess();
        grants.value = res.grants;
    } catch (e) {
        error.value = grantErrorMessage(e);
    } finally {
        loading.value = false;
    }
}

onMounted(load);

// A renewal nudge sent within the last 24h is already with the approver — the
// backend debounces a re-send, so reflect that in the UI instead of offering
// the button again.
function recentlyRequested(g: MyGrant): boolean {
    if (!g.renewalRequestedAt) return false;
    const t = new Date(g.renewalRequestedAt).getTime();
    return Number.isFinite(t) && Date.now() - t < ONE_DAY_MS;
}

async function nudge(g: MyGrant) {
    busyId.value = g._id;
    actionError.value = null;
    try {
        const res = await requestRenewal(g._id);
        // Reflect the new state locally so the button flips without a reload.
        if (res.renewalRequestedAt) g.renewalRequestedAt = res.renewalRequestedAt;
        else g.renewalRequestedAt = new Date().toISOString();
    } catch (e) {
        actionError.value = grantErrorMessage(e);
    } finally {
        busyId.value = null;
    }
}

function fmtDay(d?: string): string { return d ? formatDate(d) : '—'; }

const STATUS_CLASS: Record<GrantStatus, string> = {
    pending: 'bg-amber-900/40 text-amber-200',
    approved: 'bg-green-900/40 text-green-300',
    expired: 'bg-bp-panel-2 text-bp-muted',
    revoked: 'bg-bp-panel-2 text-bp-muted',
    rejected: 'bg-red-900/40 text-red-300',
};

const STATUS_LABEL: Record<GrantStatus, string> = {
    pending: 'Awaiting approval',
    approved: 'Active',
    expired: 'Expired',
    revoked: 'Revoked',
    rejected: 'Declined',
};
</script>

<template>
    <div class="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <h1 class="text-2xl font-semibold mb-1">My VPN access</h1>
        <p class="text-bp-muted text-sm mb-6">
            The access groups you hold and when each expires. When access has lapsed — or is
            about to — you can ask your approver to renew it. Renewal is the approver's decision.
        </p>

        <div v-if="error" class="bg-red-900/30 border border-red-700 text-red-200 px-4 py-2 rounded mb-4 text-sm">
            {{ error }}
        </div>
        <div v-if="actionError" class="bg-red-900/30 border border-red-700 text-red-200 px-4 py-2 rounded mb-4 text-sm">
            {{ actionError }}
        </div>

        <div v-if="loading" class="text-sm text-bp-muted">Loading…</div>
        <div v-else-if="grants.length === 0"
             class="bg-bp-panel border border-bp-border rounded px-4 py-6 text-sm text-bp-muted">
            You have no VPN access grants. When access is requested for you and approved, it will appear here.
        </div>

        <div v-else class="space-y-4">
            <section v-for="g in grants" :key="g._id"
                     class="bg-bp-panel border border-bp-border rounded p-4">
                <div class="flex items-start gap-3">
                    <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2 flex-wrap">
                            <h2 class="font-semibold text-bp-text truncate">{{ g.group?.name || 'Access group' }}</h2>
                            <span class="px-2 py-0.5 rounded text-xs font-medium" :class="STATUS_CLASS[g.status]">
                                {{ STATUS_LABEL[g.status] }}
                            </span>
                        </div>
                        <p class="text-sm text-bp-muted mt-1">
                            <span v-if="g.status === 'approved'">Expires {{ fmtDay(g.expiresAt) }}</span>
                            <span v-else-if="g.status === 'expired'">Expired {{ fmtDay(g.expiresAt) }}</span>
                            <span v-else-if="g.status === 'pending'">Requested {{ fmtDay(g.createdAt) }}</span>
                            <span v-else>Requested {{ fmtDay(g.createdAt) }}</span>
                        </p>
                    </div>

                    <div class="flex-shrink-0 text-right">
                        <template v-if="g.canRequestRenewal">
                            <button v-if="!recentlyRequested(g)" type="button"
                                    :disabled="busyId === g._id"
                                    class="px-3 py-1.5 rounded bg-brand-bright-blue text-white text-sm hover:bg-brand-bright-blue/90 disabled:opacity-50"
                                    @click="nudge(g)">
                                {{ busyId === g._id ? 'Sending…' : 'Request renewal' }}
                            </button>
                            <p v-else class="text-xs text-bp-muted max-w-[12rem]">
                                Renewal requested {{ fmtDay(g.renewalRequestedAt) }} — your approver has been notified.
                            </p>
                        </template>
                    </div>
                </div>

                <div class="border-t border-bp-border mt-3 pt-3">
                    <p class="text-xs font-medium text-bp-muted mb-1.5">Sites this grants access to</p>
                    <p v-if="!g.sites.length" class="text-sm text-bp-muted">
                        No sites are reachable through this group yet.
                    </p>
                    <ul v-else class="text-sm divide-y divide-bp-border">
                        <li v-for="s in g.sites" :key="s.id" class="py-1.5 text-bp-text">{{ s.name }}</li>
                    </ul>
                </div>
            </section>
        </div>
    </div>
</template>
